import React, { useMemo, useState, useEffect, useRef } from 'react';
import { User, CreditCard, Check, Pencil, Trash2, ArrowLeft, FileSignature, BookMarked } from 'lucide-react';
import { MigratePanel } from '../components/migration/MigratePanel';
import { readBooksOpening } from '../lib/booksOpeningStore';
import { defaultAdminEntryDate, resolveWorkspaceAsOfDate } from '../lib/workspaceAsOf';
import { toast } from 'react-toastify';
import { appendDevLog, loadDevJson, removeDevKey, saveDevJson } from '../lib/devStorage';
import {
  deleteHandLoanRecord,
  isLoanRecordPending,
  pendingLoanRecordIds,
  readHandLoanRecords,
  serializeHandLoanForm,
  sortByEntryDateDesc,
  formatWorkspaceEntryDate,
  upsertHandLoanRecord,
  useHandLoanRecords,
  type HandLoanRecord,
} from '../lib/workspaceStore';
import { useTranslation } from '../lib/i18n';
import { PageSearchBar } from '../components/common/PageSearchBar';
import DateRangeFilter from '../components/common/DateRangeFilter';
import { useDateRange, inRange, formatRangeLabel } from '../lib/dateFilter';
import { ExportMenu, type ExportRow } from '../lib/exportReports';
import { exportLoanApplicationPdf } from '../lib/loanApplicationPdf';
import { useScrollToTop } from '../lib/useScrollToTop';
import { useCanWrite } from '../lib/permissions';
import { PaymentViaFields, validatePaymentVia } from '../components/common/PaymentViaFields';
import { readCompanyBanks } from '../lib/financeCatalogs';
import { resolveBankLabel } from '../lib/paymentVia';
import { CardMetric } from '../components/common/ResponsiveCardValue';
import PhotoUpload from '../components/common/PhotoUpload';
import { mediaSrc } from '../lib/mediaUrl';
import { AmountInput } from '../components/common/AmountInput';
import { parseAmountInput } from '../lib/formatInr';
import { digitsOnly, isValidAadhaar12, aadhaarHint } from '../lib/inputRules';
import {
  computeEmiHandLoan,
  computeShortTermHandLoan,
  formatInr,
  HAND_LOAN_RATE_LABEL,
  handLoanRepaymentLabel,
  parseHandLoanRepaymentType,
  type HandLoanRepaymentType,
} from '../lib/handLoanMath';
import {
  handLoanInterestTypeLabel,
  parseHandLoanInterestType,
  type HandLoanInterestType,
} from '../lib/handLoanBalances';

type Step = 'personal' | 'loan';
type LoanType = 'PERSONAL' | 'VEHICLE';

const flowSteps: { key: Step; labelKey: string }[] = [
  { key: 'personal', labelKey: 'loans.stepBorrower' },
  { key: 'loan', labelKey: 'loans.stepFacility' },
];

const HAND_DRAFT_KEY = 'handloan_draft_v1';
const HAND_LOG_KEY = 'handloan_submit_log';

function emptyHandForm() {
  return {
    name: '',
    dob: '',
    age: '',
    aadharNumber: '',
    panNumber: '',
    address: '',
    aadharOriginal: null as File | null,
    supportingDocs: null as FileList | null,
    suretyName: '',
    suretyAddress: '',
    suretyNumber: '',
    suretyImage: null as File | null,
    suretyAadhar: null as File | null,
    loanAmount: '',
    interestRate: '',
    interestType: 'simple' as HandLoanInterestType,
    agreementDate: defaultAdminEntryDate() || resolveWorkspaceAsOfDate(),
    repaymentType: 'short_term' as HandLoanRepaymentType,
    loanTerm: '',
    loanType: 'PERSONAL' as LoanType,
    disbursementVia: '' as '' | 'cash' | 'online' | 'split',
    disbursementBankId: '',
    disbursementBankLabel: '',
    disbursementPaidBy: '',
    disbursementCashAmount: '',
    disbursementBankAmount: '',
    applicantPhoto: '',
    loanDetails: new Map<string, string>(),
  };
}

function snapshotToHand(data: Record<string, unknown>) {
  const base = emptyHandForm();
  const raw = data.loanDetails;
  const map =
    raw && typeof raw === 'object' && !Array.isArray(raw)
      ? new Map(Object.entries(raw as Record<string, string>))
      : new Map<string, string>();
  (Object.keys(base) as (keyof typeof base)[]).forEach((k) => {
    if (k === 'loanDetails' || k === 'aadharOriginal' || k === 'supportingDocs' || k === 'suretyImage' || k === 'suretyAadhar')
      return;
    if (k in data && data[k] !== undefined) {
      (base as Record<string, unknown>)[k] = data[k] as never;
    }
  });
  base.loanDetails = map;
  if (!base.repaymentType) {
    base.repaymentType = parseHandLoanRepaymentType(data.repaymentType, data.interestBasis);
  }
  base.interestType = parseHandLoanInterestType(data.interestType);
  if (!base.agreementDate) {
    base.agreementDate = String(data.agreementDate || data.startDate || '').slice(0, 10);
  }
  return base;
}

export function HandLoans({ setActiveItem }: { setActiveItem: (item: string) => void }) {
  const [pageMode, setPageMode] = useState<'list' | 'wizard' | 'migrate'>('list');
  const [currentStep, setCurrentStep] = useState<Step>('personal');
  const [formData, setFormData] = useState(emptyHandForm);
  const [calculatedEmi, setCalculatedEmi] = useState<number | null>(null);
  const [editingHandId, setEditingHandId] = useState<string | null>(null);
  /** Allocated for new applications so photos upload as customer_<id> before save. */
  const [draftClientId, setDraftClientId] = useState<string | null>(null);
  const customerPhotoId = editingHandId ?? draftClientId;
  const draftRestored = useRef(false);
  const listAnchorRef = useRef<HTMLElement>(null);
  const handLoans = useHandLoanRecords();
  const { t } = useTranslation();
  const canWrite = useCanWrite('loans');
  // Recompute the pending-dues set whenever the hand-loan records change.
  const pendingIds = useMemo(() => {
    void handLoans;
    return pendingLoanRecordIds();
  }, [handLoans]);
  const [tableSearch, setTableSearch] = useState('');
  const dr = useDateRange('all');

  // Opening the new/edit wizard and advancing steps should start at the top.
  useScrollToTop([pageMode, pageMode === 'wizard' ? currentStep : null]);

  const filteredHands = useMemo(() => {
    const q = tableSearch.trim().toLowerCase();
    return sortByEntryDateDesc(
      handLoans.filter((r) => {
        if (!inRange(r.createdAt, dr.range)) return false;
        if (!q) return true;
        const d = r.data;
        const hay = [
          String(d.name ?? ''),
          String(d.loanAmount ?? ''),
          String(d.loanTerm ?? ''),
          String(d.loanType ?? ''),
          String(d.aadharNumber ?? ''),
          String(d.panNumber ?? ''),
        ]
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      })
    );
  }, [handLoans, tableSearch, dr.range]);

  const handExportRows: ExportRow[] = useMemo(
    () =>
      filteredHands.map((r) => {
        const d = r.data;
        return {
          Borrower: String(d.name ?? ''),
          Amount: String(d.loanAmount ?? ''),
          Term: String(d.loanTerm ?? ''),
          Type: String(d.loanType ?? ''),
          Interest: String(d.interestRate ?? ''),
          'Interest type': handLoanInterestTypeLabel(parseHandLoanInterestType(d.interestType)),
          'Repayment type': String(d.repaymentType ?? 'short_term'),
          Aadhaar: String(d.aadharNumber ?? ''),
          Created: formatWorkspaceEntryDate(r.createdAt),
        };
      }),
    [filteredHands],
  );

  useEffect(() => {
    const saved = loadDevJson<Record<string, unknown> | null>(HAND_DRAFT_KEY, null);
    if (!saved || draftRestored.current) return;
    draftRestored.current = true;
    const ld = saved.loanDetails;
    const map =
      ld && typeof ld === 'object' && !Array.isArray(ld)
        ? new Map(Object.entries(ld as Record<string, string>))
        : new Map<string, string>();
    const { loanDetails: _x, ...rest } = saved;
    setFormData((prev) => ({
      ...prev,
      ...rest,
      loanDetails: map,
      aadharOriginal: null,
      supportingDocs: null,
      suretyImage: null,
      suretyAadhar: null,
    } as typeof prev));
    if (rest.name || rest.loanAmount) {
      setDraftClientId(crypto.randomUUID());
      toast.info('Restored hand-loan draft from this device (localStorage).');
      setPageMode('wizard');
    }
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => {
      const { aadharOriginal, supportingDocs, suretyImage, suretyAadhar, loanDetails, ...rest } = formData;
      saveDevJson(HAND_DRAFT_KEY, {
        ...rest,
        loanDetails: Object.fromEntries(loanDetails),
      });
    }, 450);
    return () => window.clearTimeout(t);
  }, [formData]);

  const openNewApplication = () => {
    if (!canWrite) return;
    setEditingHandId(null);
    setDraftClientId(crypto.randomUUID());
    setFormData(emptyHandForm());
    setCurrentStep('personal');
    setCalculatedEmi(null);
    setPageMode('wizard');
  };

  const openMigrateApplication = () => {
    if (!canWrite) return;
    setEditingHandId(null);
    setDraftClientId(null);
    setCalculatedEmi(null);
    setPageMode('migrate');
  };

  const backToList = () => {
    setPageMode('list');
    setEditingHandId(null);
    setDraftClientId(null);
    setFormData(emptyHandForm());
    setCurrentStep('personal');
    setCalculatedEmi(null);
  };

  const repaymentType = parseHandLoanRepaymentType(formData.repaymentType, formData.interestBasis);

  const loanCalc = useMemo(() => {
    const principal = parseAmountInput(String(formData.loanAmount));
    const rate = parseFloat(String(formData.interestRate));
    const term = parseFloat(String(formData.loanTerm));
    if (!Number.isFinite(principal) || principal <= 0) return null;
    if (!Number.isFinite(rate) || rate < 0) return null;
    if (!Number.isFinite(term) || term <= 0) return null;
    if (repaymentType === 'short_term') {
      const calc = computeShortTermHandLoan(principal, rate, term);
      return calc ? { type: 'short_term' as const, ...calc } : null;
    }
    const emiPlan = computeEmiHandLoan(principal, rate, term);
    return emiPlan ? { type: 'emi' as const, ...emiPlan } : null;
  }, [formData.loanAmount, formData.interestRate, formData.loanTerm, repaymentType]);

  const calculateEMI = () => {
    if (loanCalc?.type === 'emi') setCalculatedEmi(loanCalc.emi);
    else if (loanCalc?.type === 'short_term') setCalculatedEmi(loanCalc.totalPayable);
  };

  const handleNext = () => {
    if (currentStep === 'personal') setCurrentStep('loan');
  };

  const handleBack = () => {
    if (currentStep === 'loan') setCurrentStep('personal');
  };

  const persistHandRecord = () => {
    const id = editingHandId ?? draftClientId ?? crypto.randomUUID();
    const existing = readHandLoanRecords().find((r) => r.id === id);
    const now = new Date().toISOString();
    const agreementDate =
      (formData.agreementDate || defaultAdminEntryDate() || resolveWorkspaceAsOfDate() || now.slice(0, 10)).slice(0, 10);
    const entryIso = `${agreementDate}T12:00:00`;
    const merged = {
      ...formData,
      interestRate: formData.interestRate.trim() || '0',
      interestType: parseHandLoanInterestType(formData.interestType),
      agreementDate,
      startDate: agreementDate,
      repaymentType,
      disbursementBankLabel: resolveBankLabel(readCompanyBanks(), formData.disbursementBankId, formData.disbursementBankLabel),
    };
    const snap = serializeHandLoanForm({ ...merged } as Record<string, unknown>);
    upsertHandLoanRecord({
      id,
      createdAt: existing?.createdAt ?? entryIso,
      updatedAt: now,
      source: 'local',
      data: snap,
    });
    appendDevLog(HAND_LOG_KEY, { name: formData.name, loanAmount: formData.loanAmount, source: 'local', id });
    removeDevKey(HAND_DRAFT_KEY);
    setFormData(emptyHandForm());
    setCalculatedEmi(null);
    setEditingHandId(null);
    setDraftClientId(null);
    setCurrentStep('personal');
  };

  const createHandLoan = async () => {
    if (!canWrite) {
      toast.error('You have read-only access to Hand loans.');
      return;
    }
    if (!formData.name.trim() || !formData.loanAmount || !formData.loanTerm) {
      toast.error('Enter borrower name, loan amount, and term.');
      return;
    }
    const term = parseInt(String(formData.loanTerm), 10);
    if (!Number.isFinite(term) || term <= 0) {
      toast.error(`Term must be a positive number of ${repaymentType === 'short_term' ? 'days' : 'months'}.`);
      return;
    }
    const principal = parseAmountInput(String(formData.loanAmount));
    if (!Number.isFinite(principal) || principal <= 0) {
      toast.error('Enter a valid loan amount.');
      return;
    }
    if (formData.aadharNumber?.trim() && !isValidAadhaar12(formData.aadharNumber)) {
      toast.error('Aadhaar must be exactly 12 digits.');
      return;
    }
    const payErr = validatePaymentVia(
      {
        paymentVia: formData.disbursementVia || '',
        companyBankId: formData.disbursementBankId || '',
        paidBy: formData.disbursementPaidBy || '',
        cashAmount: formData.disbursementCashAmount || '',
        bankAmount: formData.disbursementBankAmount || '',
      },
      'disbursement',
      principal
    );
    if (payErr) {
      toast.error(payErr);
      return;
    }

    const wasEdit = !!editingHandId;
    try {
      persistHandRecord();
      toast.success(wasEdit ? 'Hand loan updated on this device.' : 'Hand loan saved on this device.');
      setPageMode('list');
      window.setTimeout(() => {
        listAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    } catch (err) {
      console.error(err);
      toast.error('Could not save — storage may be full or blocked.');
    }
  };

  const startEdit = (r: HandLoanRecord) => {
    if (!canWrite) return;
    setEditingHandId(r.id);
    setDraftClientId(null);
    setFormData(snapshotToHand(r.data));
    setCurrentStep('personal');
    setPageMode('wizard');
    toast.info('Loaded for editing — submit to save.');
  };

  const confirmDelete = (id: string) => {
    if (!canWrite) return;
    // Guard: a borrower with pending EMIs / unsettled dues can't be deleted.
    if (isLoanRecordPending(id)) {
      toast.error("Can't delete — this customer's account still has pending EMIs / dues. Settle or close the file first.");
      return;
    }
    if (!window.confirm('Delete this hand loan from the workspace?')) return;
    deleteHandLoanRecord(id);
    if (editingHandId === id) {
      setEditingHandId(null);
      setFormData(emptyHandForm());
      setCurrentStep('personal');
      setPageMode('list');
    }
    toast.success('Deleted.');
  };

  const currentIdx = flowSteps.findIndex((s) => s.key === currentStep);
  const wizardTitle = editingHandId ? 'Edit hand loan' : 'New hand loan';
  const wizardSubtitle = editingHandId
    ? 'Update borrower or facility and save — the workspace record is overwritten.'
    : 'Two steps on one page: borrower profile, then loan terms. Submit returns you to the record list.';

  const renderPersonalStep = () => (
    <div className="app-card overflow-hidden">
      <div className="app-card-inner">
        <div className="app-section-head">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center border border-emerald-800 bg-emerald-800 text-white">
              <User className="h-5 w-5" />
            </div>
            <div>
              <h2 className="app-section-title">Step 1 — Borrower</h2>
              <p className="app-section-desc">Identity and address (files are not stored in draft).</p>
            </div>
          </div>
        </div>
        <form className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-x-8 md:gap-y-6" onSubmit={(e) => e.preventDefault()}>
          <div>
            <label className="app-label">Full name</label>
            <input type="text" className="app-field" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
          </div>
          <div>
            <label className="app-label">Date of birth</label>
            <input type="date" className="app-field" value={formData.dob} onChange={(e) => setFormData({ ...formData, dob: e.target.value })} />
          </div>
          <div>
            <label className="app-label">Age</label>
            <input type="number" className="app-field" value={formData.age} onChange={(e) => setFormData({ ...formData, age: e.target.value })} />
          </div>
          <div>
            <label className="app-label">Aadhar number</label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={12}
              className="app-field"
              value={formData.aadharNumber}
              onChange={(e) => setFormData({ ...formData, aadharNumber: digitsOnly(e.target.value, 12) })}
              placeholder="12-digit Aadhaar"
            />
            <p className={`mt-1 text-[11px] ${isValidAadhaar12(formData.aadharNumber) ? 'text-emerald-700' : 'text-zinc-500'}`}>
              {aadhaarHint(formData.aadharNumber)}
            </p>
          </div>
          <div>
            <label className="app-label">PAN number</label>
            <input type="text" className="app-field" value={formData.panNumber} onChange={(e) => setFormData({ ...formData, panNumber: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <label className="app-label">Address</label>
            <textarea className="app-field min-h-[6rem] resize-y" rows={3} value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
          </div>
        </form>
        <div className="mt-6 flex justify-end border-t border-slate-200 pt-5">
          <button type="button" onClick={handleNext} className="app-btn-primary">
            Continue
          </button>
        </div>
      </div>
    </div>
  );

  const renderLoanStep = () => (
    <div className="app-card overflow-hidden">
      <div className="app-card-inner">
        <div className="app-section-head">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center border border-emerald-800 bg-emerald-800 text-white">
              <CreditCard className="h-5 w-5" />
            </div>
            <div>
              <h2 className="app-section-title">Step 2 — Facility</h2>
              <p className="app-section-desc">Principal, rate, tenor, and product type.</p>
            </div>
          </div>
        </div>
        <form
          className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-x-8 md:gap-y-6"
          onSubmit={(e) => {
            e.preventDefault();
            void createHandLoan();
          }}
        >
          <AmountInput
            id="hand-loan-amount"
            label="Loan amount (₹)"
            value={formData.loanAmount}
            onChange={(v) => setFormData({ ...formData, loanAmount: v })}
            placeholder="Principal amount"
          />

          <div className="md:col-span-2">
            <PaymentViaFields
              mode="disbursement"
              value={{
                paymentVia: formData.disbursementVia || '',
                companyBankId: formData.disbursementBankId || '',
                paidBy: formData.disbursementPaidBy || '',
                cashAmount: formData.disbursementCashAmount || '',
                bankAmount: formData.disbursementBankAmount || '',
              }}
              onChange={(v) =>
                setFormData({
                  ...formData,
                  disbursementVia: v.paymentVia,
                  disbursementBankId: v.companyBankId,
                  disbursementPaidBy: v.paidBy,
                  disbursementCashAmount: v.cashAmount,
                  disbursementBankAmount: v.bankAmount,
                  disbursementBankLabel: resolveBankLabel(readCompanyBanks(), v.companyBankId),
                })
              }
              totalAmount={Math.max(0, parseFloat(String(formData.loanAmount || '').replace(/,/g, '')) || 0)}
            />
          </div>

          <div className="md:col-span-2">
            <label className="app-label">Repayment type</label>
            <div className="flex flex-wrap gap-2">
              {(['short_term', 'emi'] as HandLoanRepaymentType[]).map((t) => {
                const active = repaymentType === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setFormData({ ...formData, repaymentType: t })}
                    className={`rounded-lg border px-4 py-2 text-sm font-semibold transition-colors ${
                      active
                        ? 'border-emerald-600 bg-emerald-600 text-white shadow-sm'
                        : 'border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50'
                    }`}
                  >
                    {handLoanRepaymentLabel(t)}
                  </button>
                );
              })}
            </div>
            <p className="mt-1.5 text-xs text-zinc-500">
              {repaymentType === 'short_term'
                ? 'Borrower repays principal + interest in one lump sum after the chosen number of days. Interest = daily rate × days.'
                : 'Same as Finance — fixed monthly EMI collections tracked on the customer profile.'}
            </p>
          </div>
          <div className="md:col-span-2">
            <label className="app-label">Interest type</label>
            <div className="flex flex-wrap gap-2">
              {(['simple', 'compound'] as HandLoanInterestType[]).map((t) => {
                const active = parseHandLoanInterestType(formData.interestType) === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setFormData({ ...formData, interestType: t })}
                    className={`rounded-lg border px-4 py-2 text-sm font-semibold transition-colors ${
                      active
                        ? 'border-emerald-600 bg-emerald-600 text-white shadow-sm'
                        : 'border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50'
                    }`}
                  >
                    {handLoanInterestTypeLabel(t)}
                  </button>
                );
              })}
            </div>
            <p className="mt-1.5 text-xs text-zinc-500">
              {parseHandLoanInterestType(formData.interestType) === 'simple'
                ? 'During the term, interest is on outstanding principal only (e.g. ₹60,000 → ₹1,800/mo; after ₹20,000 principal paid, ₹40,000 → ₹1,200/mo). After the term ends, unpaid balance compounds automatically.'
                : 'Unpaid interest is added to principal each period. After the contract term, overdue balances continue to compound.'}
            </p>
          </div>
          <div>
            <label className="app-label">Agreement / start date</label>
            <input
              type="date"
              className="app-field"
              value={formData.agreementDate}
              onChange={(e) => setFormData({ ...formData, agreementDate: e.target.value })}
              required
            />
            <p className="mt-1.5 text-xs text-zinc-500">Interest and schedules run from this date. Defaults to Working date in the navbar.</p>
          </div>
          <div>
            <label className="app-label">{HAND_LOAN_RATE_LABEL}</label>
            <input
              type="number"
              step="any"
              min="0"
              className="app-field"
              value={formData.interestRate}
              onChange={(e) => setFormData({ ...formData, interestRate: e.target.value })}
              placeholder="e.g. 3"
            />
            <p className="mt-1.5 text-xs text-zinc-500">On ₹1,00,000 @ ₹3 → ₹3,000/month → ₹100/day (÷30).</p>
          </div>
          <div>
            <label className="app-label">{repaymentType === 'short_term' ? 'Term (days)' : 'Term (months)'}</label>
            <input
              type="number"
              min="1"
              className="app-field"
              value={formData.loanTerm}
              onChange={(e) => setFormData({ ...formData, loanTerm: e.target.value })}
              placeholder={repaymentType === 'short_term' ? 'e.g. 30' : 'e.g. 12'}
            />
          </div>
          {loanCalc ? (
            <div className="rounded-xl border border-emerald-200/80 bg-emerald-50/60 p-4 dark:border-emerald-800/50 dark:bg-emerald-950/35 md:col-span-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-900/80 dark:text-emerald-300">
                {loanCalc.type === 'short_term' ? 'Short-term interest breakdown' : 'EMI plan'}
              </p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-lg border border-white/80 bg-white/90 px-3 py-2.5 dark:border-zinc-600 dark:bg-zinc-800/90">
                  <p className="text-[11px] font-medium text-zinc-500 dark:text-zinc-400">Interest / month</p>
                  <CardMetric value={formatInr(loanCalc.interestPerMonth)} className="mt-1 text-zinc-900 dark:text-zinc-100" scale="compact" />
                  <p className="mt-0.5 text-[11px] text-zinc-500 dark:text-zinc-400">principal × (rate ÷ 100)</p>
                </div>
                {loanCalc.type === 'short_term' ? (
                  <>
                    <div className="rounded-lg border border-white/80 bg-white/90 px-3 py-2.5 dark:border-zinc-600 dark:bg-zinc-800/90">
                      <p className="text-[11px] font-medium text-zinc-500 dark:text-zinc-400">Interest / day</p>
                      <CardMetric value={formatInr(loanCalc.interestPerDay)} className="mt-1 text-zinc-900 dark:text-zinc-100" scale="compact" />
                      <p className="mt-0.5 text-[11px] text-zinc-500 dark:text-zinc-400">
                        {formatInr(loanCalc.interestPerMonth)} ÷ 30 days
                      </p>
                    </div>
                    <div className="rounded-lg border border-white/80 bg-white/90 px-3 py-2.5 dark:border-zinc-600 dark:bg-zinc-800/90">
                      <p className="text-[11px] font-medium text-zinc-500 dark:text-zinc-400">Total interest</p>
                      <CardMetric value={formatInr(loanCalc.totalInterest)} className="mt-1 text-zinc-900 dark:text-zinc-100" scale="compact" />
                      <p className="mt-0.5 text-[11px] text-zinc-500 dark:text-zinc-400">
                        {formatInr(loanCalc.interestPerDay)} × {loanCalc.days} days
                      </p>
                    </div>
                    <div className="rounded-lg border border-emerald-200 bg-white px-3 py-2.5 dark:border-emerald-800/50 dark:bg-zinc-800/90">
                      <p className="text-[11px] font-medium text-emerald-800 dark:text-emerald-300">Amount to collect</p>
                      <CardMetric value={formatInr(loanCalc.totalPayable)} className="mt-1 text-emerald-900 dark:text-emerald-300" scale="compact" />
                      <p className="mt-0.5 text-[11px] text-zinc-500 dark:text-zinc-400">principal + interest</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="rounded-lg border border-white/80 bg-white/90 px-3 py-2.5 dark:border-zinc-600 dark:bg-zinc-800/90">
                      <p className="text-[11px] font-medium text-zinc-500 dark:text-zinc-400">Total interest</p>
                      <CardMetric value={formatInr(loanCalc.totalInterest)} className="mt-1 text-zinc-900 dark:text-zinc-100" scale="compact" />
                      <p className="mt-0.5 text-[11px] text-zinc-500 dark:text-zinc-400">
                        {formatInr(loanCalc.interestPerMonth)} × {loanCalc.months} mo
                      </p>
                    </div>
                    <div className="rounded-lg border border-emerald-200 bg-white px-3 py-2.5 dark:border-emerald-800/50 dark:bg-zinc-800/90 lg:col-span-2">
                      <p className="text-[11px] font-medium text-emerald-800 dark:text-emerald-300">Monthly EMI</p>
                      <CardMetric value={formatInr(loanCalc.emi)} className="mt-1 text-emerald-900 dark:text-emerald-300" scale="compact" />
                      <p className="mt-0.5 text-[11px] text-zinc-500 dark:text-zinc-400">
                        (principal + interest) ÷ {loanCalc.months} = ({formatInr(loanCalc.principal)} + {formatInr(loanCalc.totalInterest)}) ÷ {loanCalc.months}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : null}
          <div>
            <label className="app-label">Loan type</label>
            <select className="app-field" value={formData.loanType} onChange={(e) => setFormData({ ...formData, loanType: e.target.value as LoanType })}>
              <option value="PERSONAL">Personal</option>
              <option value="VEHICLE">Vehicle</option>
            </select>
          </div>
          <div className="flex flex-wrap items-center gap-3 md:col-span-2">
            <button type="button" onClick={calculateEMI} className="app-btn-secondary text-sm">
              {repaymentType === 'short_term' ? 'Show total to collect' : 'Estimate EMI'}
            </button>
            {calculatedEmi != null && loanCalc ? (
              <span className="text-sm text-slate-600 dark:text-zinc-400">
                {loanCalc.type === 'short_term' ? (
                  <>
                    Total to collect:{' '}
                    <strong className="font-mono text-slate-900 dark:text-zinc-100">₹{calculatedEmi.toLocaleString('en-IN')}</strong>
                  </>
                ) : (
                  <>
                    Indicative EMI:{' '}
                    <strong className="font-mono text-slate-900 dark:text-zinc-100">₹{calculatedEmi.toLocaleString('en-IN')}</strong>
                    /mo
                  </>
                )}
              </span>
            ) : loanCalc?.type === 'emi' ? (
              <span className="text-sm text-slate-600 dark:text-zinc-400">
                EMI: <strong className="font-mono text-slate-900 dark:text-zinc-100">₹{loanCalc.emi.toLocaleString('en-IN')}</strong>/mo
              </span>
            ) : loanCalc?.type === 'short_term' ? (
              <span className="text-sm text-slate-600 dark:text-zinc-400">
                Collect: <strong className="font-mono text-slate-900 dark:text-zinc-100">₹{loanCalc.totalPayable.toLocaleString('en-IN')}</strong>
              </span>
            ) : null}
          </div>
          <div className="md:col-span-2">
            <label className="app-label">Applicant photo</label>
            <PhotoUpload
              value={formData.applicantPhoto}
              onChange={(v) => setFormData({ ...formData, applicantPhoto: v })}
              customerId={customerPhotoId}
              label="Shown at the top-right of the printed application form."
            />
          </div>
          <div className="mt-2 flex flex-wrap justify-end gap-2 border-t border-slate-200 pt-5 md:col-span-2">
            <button type="button" onClick={handleBack} className="app-btn-secondary">
              Back
            </button>
            <button type="submit" className="app-btn-primary">
              Save to workspace
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return (
    <div className="app-page">
      {pageMode === 'migrate' ? (
        <section className="mb-4 space-y-4 sm:mb-6" aria-label="Migrate hand loan">
          <button
            type="button"
            onClick={backToList}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-800 shadow-sm hover:bg-slate-50"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
            {t('finance.backToList')}
          </button>
          <MigratePanel
            kind="handloan"
            canWrite={canWrite}
            cutoverDefault={
              readBooksOpening().cutoverDate || defaultAdminEntryDate() || resolveWorkspaceAsOfDate() || ''
            }
            onDone={backToList}
          />
        </section>
      ) : pageMode === 'wizard' ? (
        <section
          className="mb-6 min-h-[60vh] overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-md ring-1 ring-slate-900/[0.04] dark:border-zinc-700 dark:bg-zinc-900 dark:ring-zinc-700/50 sm:rounded-2xl"
          aria-label="Hand loan application"
        >
          <div className="border-b border-slate-100 bg-gradient-to-r from-emerald-50/95 via-white to-slate-50/90 px-3 py-5 dark:border-zinc-700 dark:from-zinc-900 dark:via-zinc-900 dark:to-zinc-800/90 sm:px-8 sm:py-6">
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <button
                  type="button"
                  onClick={backToList}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white/95 px-3 py-2 text-sm font-medium text-slate-800 shadow-sm transition-colors hover:bg-white dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700"
                >
                  <ArrowLeft className="h-4 w-4" aria-hidden />
                  {t('finance.backToList')}
                </button>
                <p className="mt-5 text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-700">Origination</p>
                <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50 sm:text-3xl">{wizardTitle}</h1>
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600 dark:text-zinc-400">{wizardSubtitle}</p>
                {editingHandId ? (
                  <p className="mt-3 inline-flex items-center rounded-md bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-950 ring-1 ring-amber-200/80">
                    Workspace record · {editingHandId.slice(0, 8)}…
                  </p>
                ) : null}
              </div>
              {mediaSrc(formData.applicantPhoto) ? (
                <img
                  src={mediaSrc(formData.applicantPhoto)}
                  alt="Applicant"
                  width={96}
                  height={96}
                  className="h-20 w-20 shrink-0 rounded-xl border-2 border-white object-cover shadow-md ring-1 ring-slate-200 sm:h-24 sm:w-24"
                />
              ) : null}
            </div>
          </div>
          <div className="space-y-6 px-2 py-5 sm:px-8 sm:py-8">
            <div className="app-wizard mb-2" aria-label="Hand loan progress">
              {flowSteps.map((step, i) => {
                const active = step.key === currentStep;
                const done = i < currentIdx;
                return (
                  <div
                    key={step.key}
                    className={`app-wizard-step ${active ? 'app-wizard-step-active' : ''} ${done ? 'app-wizard-step-done' : ''}`}
                  >
                    <span className={`app-wizard-num ${active ? 'app-wizard-num-active' : ''} ${done ? 'app-wizard-num-done' : ''}`}>
                      {done ? <Check className="h-4 w-4" strokeWidth={3} /> : i + 1}
                    </span>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-zinc-400">{t(step.labelKey)}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {currentStep === 'personal' && renderPersonalStep()}
            {currentStep === 'loan' && renderLoanStep()}
          </div>
        </section>
      ) : (
        <>
          <header className="app-page-header flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-700">Origination</p>
              <h1 className="app-page-title mt-1">{t('loans.currentHandLoans')}</h1>
              <p className="app-page-sub">
                Every saved loan in one table. Open the full-page flow to originate or edit.
              </p>
            </div>
            {canWrite && (
              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={openMigrateApplication}
                  className="app-btn-secondary inline-flex w-full items-center justify-center gap-2 sm:w-auto"
                >
                  <BookMarked className="h-4 w-4 shrink-0 text-emerald-700" aria-hidden />
                  {t('accounts.tabMigrateHand')}
                </button>
                <button
                  type="button"
                  onClick={openNewApplication}
                  className="app-btn-primary inline-flex w-full items-center justify-center sm:w-auto"
                >
                  {t('loans.newApplication')}
                </button>
              </div>
            )}
          </header>

          <section ref={listAnchorRef} className="app-card mb-6 overflow-hidden" aria-label="Current hand loans">
            <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50/80 px-4 py-3 dark:border-zinc-700 dark:bg-zinc-800/90">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-sm font-semibold text-slate-900 dark:text-zinc-100">{t('loans.currentHandLoans')}</h2>
                  <p className="text-xs text-slate-500 dark:text-zinc-400">
                    {handLoans.length} record(s) · {filteredHands.length} in {formatRangeLabel(dr.range)}
                  </p>
                </div>
                <PageSearchBar
                  id="handloan-table-search"
                  value={tableSearch}
                  onChange={setTableSearch}
                  placeholder="Search borrower, amount, type…"
                  className="max-w-full sm:max-w-xs"
                />
              </div>
              <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                <DateRangeFilter
                  start={dr.range.start}
                  end={dr.range.end}
                  preset={dr.preset}
                  onStart={dr.setStart}
                  onEnd={dr.setEnd}
                  onPreset={dr.setPreset}
                  onClear={dr.clear}
                  compact
                />
                <ExportMenu
                  title={`Hand Loan Records (${formatRangeLabel(dr.range)})`}
                  excelFilename="hand-loans.xls"
                  rows={handExportRows}
                />
              </div>
            </div>
            {handLoans.length === 0 ? (
              <p className="px-4 py-6 text-sm text-slate-600 dark:text-zinc-400">{t('loans.noRecords')}</p>
            ) : filteredHands.length === 0 ? (
              <p className="px-4 py-6 text-sm text-slate-600 dark:text-zinc-400">{t('loans.noRows')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="border-b border-slate-200 bg-white text-xs uppercase tracking-wide text-slate-500 dark:border-zinc-700 dark:bg-zinc-800/90 dark:text-zinc-400">
                    <tr>
                      <th className="whitespace-nowrap px-4 py-2">{t('loans.borrower')}</th>
                      <th className="whitespace-nowrap px-4 py-2">{t('common.amount')}</th>
                      <th className="whitespace-nowrap px-4 py-2">{t('loans.interest')}</th>
                      <th className="whitespace-nowrap px-4 py-2">{t('loans.term')}</th>
                      <th className="whitespace-nowrap px-4 py-2">{t('loans.type')}</th>
                      <th className="whitespace-nowrap px-4 py-2">{t('common.entryDate')}</th>
                      <th className="whitespace-nowrap px-4 py-2 text-right">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-zinc-800">
                    {filteredHands.map((r) => {
                      const d = r.data;
                      return (
                        <tr key={r.id} className="bg-white hover:bg-slate-50/80 dark:bg-zinc-900 dark:hover:bg-zinc-800/80">
                          <td className="whitespace-nowrap px-4 py-2 font-medium text-slate-900 dark:text-zinc-100">
                            <div className="flex items-center gap-2">
                              {mediaSrc(String(d.applicantPhoto || '')) ? (
                                <img
                                  src={mediaSrc(String(d.applicantPhoto || ''))}
                                  alt=""
                                  width={32}
                                  height={32}
                                  className="h-8 w-8 shrink-0 rounded-full object-cover ring-1 ring-slate-200"
                                />
                              ) : null}
                              <span>{String(d.name || '—')}</span>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-4 py-2 dark:text-zinc-200">{String(d.loanAmount || '—')}</td>
                          <td className="whitespace-nowrap px-4 py-2 dark:text-zinc-200">
                            ₹{String(d.interestRate || '—')}
                            <span className="ml-1 text-xs text-zinc-500 dark:text-zinc-400">/₹100/mo</span>
                          </td>
                          <td className="whitespace-nowrap px-4 py-2 dark:text-zinc-200">
                            {String(d.loanTerm || '—')}
                            {parseHandLoanRepaymentType(d.repaymentType, d.interestBasis) === 'short_term' ? ' days' : ' mo'}
                          </td>
                          <td className="whitespace-nowrap px-4 py-2 dark:text-zinc-200">{String(d.loanType || '—')}</td>
                          <td className="whitespace-nowrap px-4 py-2 text-xs text-slate-500 dark:text-zinc-400">
                            {formatWorkspaceEntryDate(r.createdAt)}
                          </td>
                          <td className="whitespace-nowrap px-4 py-2 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                type="button"
                                onClick={() => exportLoanApplicationPdf(d as Record<string, unknown>, 'handloan')}
                                className="inline-flex rounded border border-emerald-200 p-1.5 text-emerald-700 hover:bg-emerald-50"
                                title="View application (PDF)"
                              >
                                <FileSignature className="h-3.5 w-3.5" />
                              </button>
                              {canWrite && (
                                <>
                                  <button type="button" onClick={() => startEdit(r)} className="inline-flex rounded border border-slate-200 p-1.5 hover:bg-white" title="Edit">
                                    <Pencil className="h-3.5 w-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => confirmDelete(r.id)}
                                    disabled={pendingIds.has(r.id)}
                                    className="inline-flex rounded border border-red-200 p-1.5 text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                                    title={pendingIds.has(r.id) ? 'Account has pending EMIs / dues — settle or close before deleting' : 'Delete'}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
